import { motion } from 'framer-motion';
import { ChevronRight, ArrowRight, Users, Calendar, Clock, ChevronDown, Settings } from 'lucide-react';

function Stepper({ active }) {
  const steps = ['Basics', 'Build', 'Configure', 'Review'];
  return (
    <div className="flex items-center justify-center gap-0 py-5 px-6 bg-[#FAFAFA] rounded-2xl mb-4">
      {steps.map((s, i) => (
        <div key={s} className="flex items-center">
          <div className="flex flex-col items-center gap-1">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border-2 ${
              i < active ? 'bg-teal-600 border-teal-600 text-white' :
              i === active ? 'bg-white border-teal-600 text-teal-600' :
              'bg-white border-[#E5E7EB] text-[#737373]'
            }`}>{i < active ? '✓' : <span className="w-1.5 h-1.5 rounded-full bg-current" />}</div>
            <span className={`text-[12px] font-body ${i <= active ? 'font-medium text-[#0A0A0A]' : 'text-[#737373]'}`}>{s}</span>
          </div>
          {i < steps.length - 1 && <div className={`w-24 h-[2px] mx-2 mt-[-16px] ${i < active ? 'bg-teal-600' : 'bg-[#E5E7EB]'}`} />}
        </div>
      ))}
    </div>
  );
}

export default function SurveyConfigScene({ onNext }) {
  return (
    <div data-testid="scene-survey-config" className="flex flex-col items-center justify-start h-full px-8 pt-3 overflow-y-auto">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full max-w-3xl">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="text-[12px] font-body text-[#737373] mb-2">
          Home <ChevronRight size={12} className="inline" /> Survey <ChevronRight size={12} className="inline" /> <span className="font-medium text-[#0A0A0A]">Create new survey</span>
        </motion.div>
        <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mb-3">Create New Survey</h2>

        <Stepper active={2} />

        <div className="flex justify-between items-center mb-3">
          <span className="text-sm font-body text-[#0A0A0A]">&lt; Back</span>
          <div className="flex gap-2 text-[12px] font-body">
            <span className="text-teal-600 font-medium">Preview survey</span>
            <span className="px-3 py-1 rounded border border-[#E5E7EB] text-[#0A0A0A]">Save</span>
          </div>
        </div>

        {/* Survey info bar */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="bg-white rounded-xl border border-[#E5E7EB] p-4 mb-4 grid grid-cols-2 gap-4">
          <div><p className="text-[10px] font-body text-[#737373]">Survey title</p><p className="font-heading font-bold text-sm text-[#0A0A0A]">Q4 Patient Satisfaction Survey</p></div>
          <div><p className="text-[10px] font-body text-[#737373]">Total questions</p><p className="font-heading font-bold text-sm text-[#0A0A0A]">3</p></div>
        </motion.div>

        {/* Audience details */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
          className="bg-[#FAFAFA] rounded-2xl border border-[#E5E7EB] p-5 mb-4">
          <div className="flex items-center gap-2 mb-1">
            <Users size={16} className="text-[#0A0A0A]" />
            <h3 className="font-heading font-bold text-base text-[#0A0A0A]">Audience details</h3>
          </div>
          <p className="font-body text-xs text-[#737373] mb-3">Define the recipients of this notification</p>

          <label className="text-[12px] font-body font-bold text-[#0A0A0A] mb-1.5 block">Select audience*</label>
          <div className="px-3 py-2.5 rounded-lg border border-[#E5E7EB] bg-white text-sm font-body text-[#737373] flex items-center justify-between mb-3">
            Select user type, organisation, or category <ChevronDown size={14} />
          </div>

          <div className="flex gap-2 mb-3">
            {['Insurance Companies', 'TPAs', 'Providers'].map(t => (
              <motion.span key={t} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.8 }}
                className="px-2.5 py-1 rounded-md bg-teal-600/10 text-teal-700 text-[11px] font-body font-medium flex items-center gap-1">
                {t} <span className="text-teal-500 cursor-pointer">&times;</span>
              </motion.span>
            ))}
          </div>

          <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0 }}
            className="bg-white rounded-xl border border-[#E5E7EB] p-4 flex items-center gap-4 w-2/3">
            <div>
              <p className="font-heading font-bold text-sm text-[#0A0A0A]">Target audience</p>
              <p className="font-heading font-extrabold text-3xl text-[#0A0A0A]">278</p>
              <p className="text-[12px] font-body text-[#737373]">recipients</p>
            </div>
            <div className="ml-auto text-orange-500">
              <Users size={36} />
            </div>
          </motion.div>
        </motion.div>

        {/* Publishing schedule */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2 }}
          className="bg-[#FAFAFA] rounded-2xl border border-[#E5E7EB] p-5 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Calendar size={16} className="text-[#0A0A0A]" />
            <h3 className="font-heading font-bold text-base text-[#0A0A0A]">Publishing schedule</h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-[12px] font-body font-bold text-[#0A0A0A] mb-1 block">Start date*</label><div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-[#E5E7EB] bg-white text-[12px] font-body text-[#0A0A0A]"><Calendar size={12} />25/12/2025</div></div>
            <div><label className="text-[12px] font-body font-bold text-[#0A0A0A] mb-1 block">Start time*</label><div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-[#E5E7EB] bg-white text-[12px] font-body text-[#0A0A0A]"><Clock size={12} />10:00 AM</div></div>
            <div><label className="text-[12px] font-body font-bold text-[#0A0A0A] mb-1 block">End date*</label><div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-[#E5E7EB] bg-white text-[12px] font-body text-[#0A0A0A]"><Calendar size={12} />25/12/2029</div></div>
            <div><label className="text-[12px] font-body font-bold text-[#0A0A0A] mb-1 block">End time*</label><div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-[#E5E7EB] bg-white text-[12px] font-body text-[#0A0A0A]"><Clock size={12} />10:00 AM</div></div>
          </div>
        </motion.div>

        {/* Response settings */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5 }}
          className="bg-[#FAFAFA] rounded-2xl border border-[#E5E7EB] p-5 mb-4">
          <div className="flex items-center gap-2 mb-1">
            <Settings size={16} className="text-[#0A0A0A]" />
            <h3 className="font-heading font-bold text-base text-[#0A0A0A]">Response settings</h3>
          </div>
          <p className="font-body text-xs text-[#737373] mb-3">Control how you get responses in your survey</p>
          <div className="flex items-center justify-between mb-2">
            <div><p className="font-body font-bold text-sm text-[#0A0A0A]">Require Consent</p><p className="text-[11px] font-body text-[#737373]">Participants must agree to terms before accessing the survey</p></div>
            <div className="w-10 h-6 rounded-full bg-teal-600 flex items-center px-0.5"><div className="w-5 h-5 rounded-full bg-white ml-auto shadow" /></div>
          </div>
          <label className="text-[12px] font-body font-bold text-[#0A0A0A] mb-1 block">Consent text</label>
          <div className="px-3 py-2.5 rounded-lg border border-[#E5E7EB] bg-white text-[11px] font-body text-[#737373] leading-relaxed">
            By proceeding with this survey, I confirm that I understand the purpose of this feedback collection and consent to the use of my responses for quality improvement purposes. My responses will be kept confidential and used only in aggregate form.
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 }} className="flex justify-center mb-4">
          <button onClick={onNext} className="flex items-center gap-2 px-8 py-2.5 rounded-xl bg-teal-600 text-white font-body font-bold text-sm cursor-pointer">Next <ArrowRight size={16} /></button>
        </motion.div>
      </motion.div>
    </div>
  );
}
